import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { BsClipboardCheck, BsPlusCircle } from "react-icons/bs";
import { MdInventory2 } from "react-icons/md";

const STATUS_LABELS = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-600" },
  pending_manager_approval: { label: "Pending Manager", color: "bg-orange-100 text-orange-700" },
  pending_owner_approval: { label: "Pending Owner", color: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700" },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-700" },
  partially_fulfilled: { label: "Partial", color: "bg-indigo-100 text-indigo-700" },
  fulfilled: { label: "Fulfilled", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
  cancelled: { label: "Cancelled", color: "bg-gray-200 text-gray-500" },
};

const FarmerManagerDashboard = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReqs = async () => {
      try {
        const res = await fetch("/api/getAllRequisitions?limit=50", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          const reqs = data.requisitions || [];
          setRequisitions(reqs.slice(0, 5));
          const counts = {};
          reqs.forEach((r) => {
            counts[r.status] = (counts[r.status] || 0) + 1;
          });
          setStatusCounts(counts);
        }
      } catch (err) {
        console.error("Error fetching requisitions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReqs();
  }, [token]);

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Welcome, {user?.name}
        </h2>
        <p className="text-sm text-gray-500 mt-1">Farmer Manager Dashboard</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <button
          onClick={() => navigate("/Requisitions/New")}
          className="bg-primary text-white rounded-xl shadow p-5 flex items-center gap-4 hover:opacity-90 transition"
        >
          <BsPlusCircle className="text-3xl flex-shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-base">New Requisition</p>
            <p className="text-xs opacity-80 mt-0.5">Request items from inventory</p>
          </div>
        </button>

        <button
          onClick={() => navigate("/Catalog")}
          className="bg-white border border-gray-200 rounded-xl shadow p-5 flex items-center gap-4 hover:shadow-md transition"
        >
          <MdInventory2 className="text-3xl text-indigo-500 flex-shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-base text-gray-800">Browse Catalog</p>
            <p className="text-xs text-gray-400 mt-0.5">View available inventory items</p>
          </div>
        </button>
      </div>

      {/* Status Summary */}
      {!loading && Object.keys(statusCounts).length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">My Requisitions by Status</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusCounts).map(([status, count]) => {
              const cfg = STATUS_LABELS[status] || { label: status, color: "bg-gray-100 text-gray-600" };
              return (
                <span
                  key={status}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer ${cfg.color}`}
                  onClick={() => navigate("/MyRequisitions")}
                >
                  {cfg.label}: {count}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Requisitions */}
      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-600 uppercase">Recent Requests</h3>
          <button
            onClick={() => navigate("/MyRequisitions")}
            className="text-xs text-primary hover:underline"
          >
            View All
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : requisitions.length === 0 ? (
          <div className="text-center py-6">
            <BsClipboardCheck className="text-4xl text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No requisitions yet.</p>
            <button
              onClick={() => navigate("/Requisitions/New")}
              className="mt-3 text-sm text-primary hover:underline"
            >
              Create your first request
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {requisitions.map((req) => {
              const cfg = STATUS_LABELS[req.status] || { label: req.status, color: "bg-gray-100 text-gray-600" };
              return (
                <div
                  key={req._id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-2 rounded"
                  onClick={() => navigate(`/Requisitions/View/${req._id}`)}
                >
                  <div>
                    <p className="text-sm font-medium text-primary">{req.requisitionNumber}</p>
                    <p className="text-xs text-gray-400">
                      {req.items?.length || 0} item(s) · {new Date(req.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerManagerDashboard;
